import {
  formatDuration,
  parseDuration,
  SOCIAL_FORMAT_LABELS,
  SOCIAL_FORMATS,
  SOCIAL_NETWORK_LABELS,
  SOCIAL_NETWORKS,
  SOCIAL_PRIORITY_LABELS,
  socialFormatSchema,
  socialPrioritySchema,
  type SocialFormat,
  type SocialNetwork,
  type SocialPiece,
  type SocialPieceInput,
  type SocialPlanEvent,
  type SocialPriority,
} from '@growup/site-shared';
import { Button, Checkbox, DateInput, Field, Input, Notice, Select, Textarea } from '@growup/ui';
import { useState, type FormEvent } from 'react';

import { ApiError, messageOf } from '../api';

/** Os dados de uma peca, para criar ou mudar (so o gestor). */
export function PieceForm({
  piece,
  event = null,
  zones = [],
  submitLabel,
  onSubmit,
  onCancel,
}: {
  piece: SocialPiece | null;
  /** O evento do plano: da o dia e a zona da peca. */
  event?: SocialPlanEvent | null;
  zones?: { id: string; name: string }[];
  submitLabel: string;
  onSubmit: (payload: SocialPieceInput) => Promise<void>;
  onCancel?: () => void;
}) {
  const [title, setTitle] = useState(piece?.title ?? '');
  const [format, setFormat] = useState<SocialFormat>(piece?.format ?? 'vertical');
  const [networks, setNetworks] = useState<SocialNetwork[]>(piece?.networks ?? []);
  const [duration, setDuration] = useState(
    piece?.durationSeconds ? formatDuration(piece.durationSeconds) : '',
  );
  const [priority, setPriority] = useState<SocialPriority>(piece?.priority ?? 'must');
  const [publishOn, setPublishOn] = useState(piece?.publishOn ?? '');
  const [description, setDescription] = useState(piece?.description ?? '');
  const [references, setReferences] = useState(piece?.referenceUrls.join('\n') ?? '');
  const [notes, setNotes] = useState(piece?.notes ?? '');
  const [eventDay, setEventDay] = useState(piece?.eventDay ?? '');
  const [zoneId, setZoneId] = useState(piece?.zone?.id ?? '');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const seconds = duration.trim() === '' ? null : parseDuration(duration);
    if (duration.trim() !== '' && seconds === null) {
      setFields({ durationSeconds: 'escreva m:ss, por exemplo 0:45' });
      return;
    }
    setBusy(true);
    setError(null);
    setFields({});
    try {
      await onSubmit({
        title,
        format,
        networks,
        durationSeconds: seconds,
        priority,
        publishOn: publishOn === '' ? null : publishOn,
        description,
        referenceUrls: references
          .split('\n')
          .map((l) => l.trim())
          .filter((l) => l !== ''),
        notes,
        eventDay: event && eventDay !== '' ? eventDay : null,
        zoneId: event && zoneId !== '' ? zoneId : null,
      });
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
      <Field label="Peça" error={fields.title}>
        <Input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Formato" error={fields.format}>
          <Select
            value={format}
            onChange={(e) => setFormat(socialFormatSchema.parse(e.target.value))}
          >
            {SOCIAL_FORMATS.map((f) => (
              <option key={f} value={f}>
                {SOCIAL_FORMAT_LABELS[f]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Duração aproximada" optional hint="m:ss" error={fields.durationSeconds}>
          <Input value={duration} maxLength={6} onChange={(e) => setDuration(e.target.value)} />
        </Field>
        <Field label="Prioridade">
          <Select
            value={priority}
            onChange={(e) => setPriority(socialPrioritySchema.parse(e.target.value))}
          >
            {(['must', 'nice'] as const).map((p) => (
              <option key={p} value={p}>
                {SOCIAL_PRIORITY_LABELS[p]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Publicar a" optional error={fields.publishOn}>
          <DateInput value={publishOn} onChange={setPublishOn} />
        </Field>
      </div>
      {event ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Dia do evento" optional hint="Quando se filma." error={fields.eventDay}>
            <Select value={eventDay} onChange={(e) => setEventDay(e.target.value)}>
              <option value="">—</option>
              {event.days.map((d) => (
                <option key={d} value={d}>
                  {d.split('-').reverse().join('/')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Zona" optional hint="Do separador Espaço do evento." error={fields.zoneId}>
            <Select value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
              <option value="">—</option>
              {zones.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      ) : null}
      <fieldset className="flex flex-col gap-2">
        <legend className="font-sub text-label font-bold uppercase">Redes alvo</legend>
        <div className="flex flex-wrap gap-4">
          {SOCIAL_NETWORKS.map((n) => (
            <Checkbox
              key={n}
              label={SOCIAL_NETWORK_LABELS[n]}
              checked={networks.includes(n)}
              onChange={(e) =>
                setNetworks((list) =>
                  SOCIAL_NETWORKS.filter((x) => (x === n ? e.target.checked : list.includes(x))),
                )
              }
            />
          ))}
        </div>
      </fieldset>
      <Field label="Descrição" optional hint="O guião ou o que se quer captar.">
        <Textarea
          rows={4}
          value={description}
          maxLength={4000}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <Field
        label="Referências"
        optional
        hint="Links https://, um por linha."
        error={fields.referenceUrls}
      >
        <Textarea rows={2} value={references} onChange={(e) => setReferences(e.target.value)} />
      </Field>
      <Field label="Notas" optional>
        <Textarea
          rows={2}
          value={notes}
          maxLength={2000}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={busy}>
          {submitLabel}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}
